import React from 'react';

import { Box, Fade, Paper, Typography } from '@material-ui/core';

import { AppSwitch, OnboardingStepTitle } from '@vyce/core/src/components';
import { PhoneOrEmail } from '@vyce/core/src/views/invites/components/PhoneOrEmail';
import { BulkPhoneEntryApi } from '@vyce/core/src/views/invites/types';
import { CompanyWorkerItem, Team, TimeAndAttendanceLocation } from '@vyce/core/src/types';

import config from '../../../api/config';
import { LocationAudience, RecipientScope } from './FormRecipientsModeStep';
import { FormTeamsStep } from './FormTeamsStep';
import { FormLocationsStep } from './FormLocationsStep';
import { FormSpecificContacts } from './FormSpecificContacts';
import { FormLocationAudience } from './FormLocationAudience';
import { LocationAudienceCounts } from '../hooks/useLocationAudience';

const SCOPE_TITLE: Record<RecipientScope, string> = {
  manual: 'Specific contacts',
  location: 'Locations',
  team: 'Teams',
};

interface Props {
  availableScopes: RecipientScope[];
  recipientScope: RecipientScope;
  setRecipientScope: (scope: RecipientScope) => void;
  companyId?: string;
  selectedWorkers?: CompanyWorkerItem[];
  toggleWorker?: (worker: CompanyWorkerItem) => void;
  teams: Team[];
  locations: TimeAndAttendanceLocation[];
  selectedTeams: Team[];
  setSelectedTeams: (teams: Team[]) => void;
  selectedLocations: TimeAndAttendanceLocation[];
  setSelectedLocations: (locations: TimeAndAttendanceLocation[]) => void;
  isPhoneInputDisabled: boolean;
  handleEmailFieldKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  handlePhoneFieldKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  addEmailToList: () => void;
  addPhoneToList: () => void;
  deleteContact: (contact: string) => void;
  emails: string[];
  phones: string[];
  setIsPhoneInputDisabled: (isPhoneInputDisabled: boolean) => void;
  bulkPaste?: BulkPhoneEntryApi;
  bulkLimitHint?: string;
  locationAudience: LocationAudience;
  setLocationAudience: (audience: LocationAudience) => void;
  locationAudienceCounts: LocationAudienceCounts;
  locationAudienceLoading: boolean;
}

export const FormRecipientsModeCard: React.FC<Props> = ({
  availableScopes,
  recipientScope,
  setRecipientScope,
  companyId,
  selectedWorkers = [],
  toggleWorker,
  teams,
  locations,
  selectedTeams,
  setSelectedTeams,
  selectedLocations,
  setSelectedLocations,
  isPhoneInputDisabled,
  handleEmailFieldKeyDown,
  handlePhoneFieldKeyDown,
  addEmailToList,
  addPhoneToList,
  deleteContact,
  emails,
  phones,
  setIsPhoneInputDisabled,
  bulkPaste,
  bulkLimitHint,
  locationAudience,
  setLocationAudience,
  locationAudienceCounts,
  locationAudienceLoading,
}) => {
  const options = availableScopes
    .filter(scope => scope !== 'team' || !config.comingSoon)
    .map(scope => ({
      value: scope,
      content: <Typography style={{ fontSize: 14, fontWeight: 600 }}>{SCOPE_TITLE[scope]}</Typography>,
    }));

  return (
    <Fade in={true} timeout={200}>
      <div>
        <Paper variant="outlined" style={{ padding: 24 }}>
          <OnboardingStepTitle
            text="Select the recipients."
            additionalText="Assign this form to specific contacts, to everyone at a Location, or to everyone on a Team."
          />

          <Box marginTop={2}>
            <AppSwitch
              options={options}
              value={recipientScope}
              onChange={value => setRecipientScope(value as RecipientScope)}
            />
          </Box>

          <Box marginTop={3}>
            {recipientScope === 'manual' &&
              (companyId && toggleWorker ? (
                <FormSpecificContacts
                  companyId={companyId}
                  selectedWorkers={selectedWorkers}
                  toggleWorker={toggleWorker}
                  isPhoneInputDisabled={isPhoneInputDisabled}
                  handleEmailFieldKeyDown={handleEmailFieldKeyDown}
                  handlePhoneFieldKeyDown={handlePhoneFieldKeyDown}
                  addEmailToList={addEmailToList}
                  addPhoneToList={addPhoneToList}
                  deleteContact={deleteContact}
                  emails={emails}
                  phones={phones}
                  setIsPhoneInputDisabled={setIsPhoneInputDisabled}
                  bulkPaste={bulkPaste}
                  bulkLimitHint={bulkLimitHint}
                />
              ) : (
                <PhoneOrEmail
                  isPhoneInputDisabled={isPhoneInputDisabled}
                  handleEmailFieldKeyDown={handleEmailFieldKeyDown}
                  handlePhoneFieldKeyDown={handlePhoneFieldKeyDown}
                  addEmailToList={addEmailToList}
                  addPhoneToList={addPhoneToList}
                  deleteContact={deleteContact}
                  emails={emails}
                  phones={phones}
                  setIsPhoneInputDisabled={setIsPhoneInputDisabled}
                  bulkPaste={bulkPaste}
                  bulkLimitHint={bulkLimitHint}
                />
              ))}

            {recipientScope === 'location' && (
              <>
                <Box maxHeight={360} style={{ overflowY: 'auto' }}>
                  <FormLocationsStep
                    embedded
                    locations={locations}
                    selectedLocations={selectedLocations}
                    setSelectedLocations={setSelectedLocations}
                  />
                </Box>

                {selectedLocations.length > 0 && (
                  <Box marginTop={3}>
                    <FormLocationAudience
                      audience={locationAudience}
                      setAudience={setLocationAudience}
                      counts={locationAudienceCounts}
                      loading={locationAudienceLoading}
                    />
                  </Box>
                )}
              </>
            )}

            {recipientScope === 'team' && (
              <Box maxHeight={360} style={{ overflowY: 'auto' }}>
                <FormTeamsStep
                  embedded
                  teams={teams}
                  selectedTeams={selectedTeams}
                  setSelectedTeams={setSelectedTeams}
                />
              </Box>
            )}
          </Box>
        </Paper>
      </div>
    </Fade>
  );
};
